/**
 * 3-dot context menu for Game Instance (Adventure) cards.
 * Options depend on ownership and multiplayer status.
 */
import React, { useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  MoreVertical,
  Play,
  Check,
  UserPlus,
  LogOut,
  Trash2,
  Download,
  Loader2,
  Shield,
  User,
} from "lucide-react";
import { ConfirmDelete } from "@/components/ui/confirm-delete";
import { exportPlayerData, exportGameInstance } from "@/lib/export-utils";

export interface AdventureCardActionsProps {
  adventureId: string;
  adventureName: string;
  /** Whether the current user owns this adventure */
  isOwner: boolean;
  /** Whether this is a multiplayer adventure */
  isMultiplayer: boolean;
  /** Invite code for MP adventures */
  inviteCode: string | null;
  onContinue: () => void;
  onInvite?: () => void;
  onLeave?: () => void;
  onDelete?: () => void;
}

export function AdventureCardActions({
  adventureId,
  adventureName,
  isOwner,
  isMultiplayer,
  inviteCode,
  onContinue,
  onInvite,
  onLeave,
  onDelete,
}: AdventureCardActionsProps) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const [exportingPlayer, setExportingPlayer] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);

  const handleCopyInvite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!inviteCode) return;
    navigator.clipboard.writeText(`${window.location.origin}/join/${inviteCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPlayer = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (exportingPlayer) return;
    setExportingPlayer(true);
    try {
      await exportPlayerData(adventureId, adventureName, t);
    } finally {
      setExportingPlayer(false);
    }
  };

  const handleExportFull = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (exportingFull) return;
    setExportingFull(true);
    try {
      await exportGameInstance(adventureId, adventureName, t);
    } finally {
      setExportingFull(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Adventure options"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
        {/* Continue Playing */}
        <DropdownMenuItem onClick={onContinue} className="gap-2 text-xs">
          <Play className="w-3.5 h-3.5" />
          Continue Playing
        </DropdownMenuItem>

        {/* Invite (MP only) */}
        {isMultiplayer && inviteCode && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleCopyInvite} className="gap-2 text-xs">
              {copied ? (
                <Check className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <UserPlus className="w-3.5 h-3.5" />
              )}
              {copied ? "Link Copied!" : "Copy Invite Link"}
            </DropdownMenuItem>
          </>
        )}

        {/* Export section */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleExportPlayer}
          disabled={exportingPlayer}
          className="gap-2 text-xs"
        >
          {exportingPlayer ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <User className="w-3.5 h-3.5" />
          )}
          Export Player Data
        </DropdownMenuItem>

        {isOwner && (
          <DropdownMenuItem
            onClick={handleExportFull}
            disabled={exportingFull}
            className="gap-2 text-xs"
          >
            {exportingFull ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            Export Full Game
          </DropdownMenuItem>
        )}

        {/* Leave (joined, non-owner) */}
        {!isOwner && onLeave && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLeave}
              className="gap-2 text-xs text-destructive focus:text-destructive"
            >
              <LogOut className="w-3.5 h-3.5" />
              Leave Game
            </DropdownMenuItem>
          </>
        )}

        {/* Delete (owner only) */}
        {isOwner && onDelete && (
          <>
            <DropdownMenuSeparator />
            <ConfirmDelete
              onConfirm={onDelete}
              title="Delete adventure?"
              description={`"${adventureName}" and all its messages, memories, and progress will be permanently deleted.`}
              trigger={
                <button className="flex items-center gap-2 w-full text-xs text-destructive hover:text-destructive px-2 py-1.5 rounded-sm hover:bg-accent cursor-default">
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Game
                </button>
              }
            />
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
